import { learningService } from '@/services/learning.service'
import type {
  LearningContentEvent,
  LearningModule,
  LearningModuleWithProgress,
  LearningTopicDetail,
  LearningTopicGeneratingEvent,
} from '@/types/learning'

export function isPendingChatGeneratedTopic(
  topicId: number,
  state: {
    learningTopicGenerating: LearningTopicGeneratingEvent | null
    learningContent: LearningContentEvent | null
  },
): boolean {
  if (state.learningTopicGenerating?.topic_id === topicId) return true
  if (state.learningContent?.generating_topic_id === topicId) return true
  if (
    state.learningContent?.topic_id === topicId &&
    state.learningContent.module_id == null &&
    !state.learningContent.content
  ) {
    return true
  }
  return false
}

export function buildLearningContentEvent(
  detail: LearningTopicDetail,
  module: LearningModule,
  moduleProgress: LearningModuleWithProgress | undefined,
): LearningContentEvent {
  return {
    module_id: module.id,
    topic_id: detail.id,
    topic_title: detail.title,
    module_title: module.title,
    content: module.text_content || '',
    order_index: module.order_index,
    total_modules: detail.modules.length,
    progress: moduleProgress
      ? {
          status: moduleProgress.status,
          progress_percent: moduleProgress.progress_percent,
        }
      : { status: 'in_progress', progress_percent: 0 },
  }
}

async function loadModuleContent(
  detail: LearningTopicDetail,
  moduleProgress: LearningModuleWithProgress,
): Promise<LearningContentEvent> {
  const fullModule = await learningService.getModule(moduleProgress.id)

  let progress = moduleProgress
  if (moduleProgress.status === 'not_started') {
    await learningService.updateProgress(moduleProgress.id, {
      status: 'in_progress',
      progress_percent: 0,
    })
    progress = { ...moduleProgress, status: 'in_progress', progress_percent: 0 }
  }

  return buildLearningContentEvent(detail, fullModule, progress)
}

export async function loadFirstModuleForTopic(
  topicId: number,
): Promise<LearningContentEvent | null> {
  const detail = await learningService.getTopic(topicId)
  if (detail.modules.length === 0) return null

  const firstModule = [...detail.modules].sort((a, b) => a.order_index - b.order_index)[0]
  if (!firstModule) return null

  return loadModuleContent(detail, firstModule)
}

export async function loadNextModuleForTopic(
  topicId: number,
  currentModuleId: number,
): Promise<LearningContentEvent | null> {
  const detail = await learningService.getTopic(topicId)
  const sorted = [...detail.modules].sort((a, b) => a.order_index - b.order_index)
  const currentIndex = sorted.findIndex((module) => module.id === currentModuleId)
  if (currentIndex === -1 || currentIndex >= sorted.length - 1) return null

  const nextModule = sorted[currentIndex + 1]
  if (!nextModule) return null

  return loadModuleContent(detail, nextModule)
}
